const SECTION_CODES = {
  navbar: "n",
  hero: "h",
  spotlight: "s",
  featured: "f",
  brands: "b",
  categories: "g",
  about: "a",
  highlights: "x",
  contact: "c",
  footer: "o",
  custom: "u",
};

const CODE_SECTIONS = Object.fromEntries(
  Object.entries(SECTION_CODES).map(([section, code]) => [code, section])
);

export const DEFAULT_SEGMENT_VISUAL_STYLE = Object.freeze({
  titleSize: 2,
  imageSize: 2,
  spacing: 2,
  textStyle: 2,
});

const ORDER = [
  "navbar",
  "hero",
  "spotlight",
  "featured",
  "brands",
  "categories",
  "about",
  "highlights",
  "contact",
  "footer",
  "custom",
];

function level(value, fallback = 2) {
  const parsed = Number(value);
  return [1, 2, 3].includes(parsed) ? parsed : fallback;
}

export function splitVisualTemplate(template) {
  const raw = String(template || "catalog-basic").trim() || "catalog-basic";
  const marker = raw.indexOf("@");
  return {
    base: (marker >= 0 ? raw.slice(0, marker) : raw) || "catalog-basic",
    encoded: marker >= 0 ? raw.slice(marker + 1) : "",
  };
}


const IMAGE_POSITION_ALIASES = Object.freeze({
  center: "center",
  centre: "center",
  middle: "center",
  "center center": "center",
  "50% 50%": "center",
  top: "top",
  "top center": "top",
  "center top": "top",
  bottom: "bottom",
  "bottom center": "bottom",
  "center bottom": "bottom",
  left: "left",
  "left center": "left",
  "center left": "left",
  right: "right",
  "right center": "right",
  "center right": "right",
});

const VISUAL_STYLE_ALIASES = Object.freeze({
  "1": "style-1",
  classic: "style-1",
  clasica: "style-1",
  default: "style-1",
  "catalog-basic": "style-1",
  "style-1": "style-1",
  "2": "style-2",
  commercial: "style-2",
  comercial: "style-2",
  split: "style-2",
  "catalog-commercial": "style-2",
  "style-2": "style-2",
  "3": "style-3",
  minimal: "style-3",
  minimalista: "style-3",
  dark: "style-3",
  "catalog-minimal": "style-3",
  "style-3": "style-3",
});

export function normalizeImagePosition(value, fallback = "center") {
  const key = String(value || "").trim().toLowerCase();
  const fallbackKey = String(fallback || "center").trim().toLowerCase();
  return IMAGE_POSITION_ALIASES[key] || IMAGE_POSITION_ALIASES[fallbackKey] || "center";
}

export function normalizeVisualStyle(value, fallback = "style-1") {
  const key = String(value || "").trim().toLowerCase();
  return VISUAL_STYLE_ALIASES[key] || VISUAL_STYLE_ALIASES[fallback] || "style-1";
}

export function visualStyleFromTemplate(template, fallback = "style-1") {
  const { base } = splitVisualTemplate(template);
  return normalizeVisualStyle(base, fallback);
}

export function parseSegmentVisualStyles(template) {
  const { encoded } = splitVisualTemplate(template);
  const styles = {};
  const pattern = /([nhsfgbaxcou])([123])([123])([123])([123])/g;
  let match;

  while ((match = pattern.exec(encoded.replace(/\s+/g, "")))) {
    const section = CODE_SECTIONS[match[1]];
    if (!section) continue;
    styles[section] = {
      titleSize: level(match[2]),
      imageSize: level(match[3]),
      spacing: level(match[4]),
      textStyle: level(match[5]),
    };
  }

  return styles;
}

export function getSegmentVisualStyle(themeOrTemplate, sectionId) {
  const template =
    typeof themeOrTemplate === "string"
      ? themeOrTemplate
      : themeOrTemplate?.template;
  return {
    ...DEFAULT_SEGMENT_VISUAL_STYLE,
    ...(parseSegmentVisualStyles(template)[sectionId] || {}),
  };
}

export function updateSegmentVisualStyle(template, sectionId, patch = {}) {
  if (!SECTION_CODES[sectionId]) return String(template || "catalog-basic");
  const { base } = splitVisualTemplate(template);
  const current = parseSegmentVisualStyles(template);
  const nextStyle = {
    ...DEFAULT_SEGMENT_VISUAL_STYLE,
    ...(current[sectionId] || {}),
    ...patch,
  };
  nextStyle.titleSize = level(nextStyle.titleSize);
  nextStyle.imageSize = level(nextStyle.imageSize);
  nextStyle.spacing = level(nextStyle.spacing);
  nextStyle.textStyle = level(nextStyle.textStyle);

  const isDefault = Object.keys(DEFAULT_SEGMENT_VISUAL_STYLE).every(
    (key) => nextStyle[key] === DEFAULT_SEGMENT_VISUAL_STYLE[key]
  );
  if (isDefault) delete current[sectionId];
  else current[sectionId] = nextStyle;

  const encoded = ORDER.map((section) => {
    const style = current[section];
    if (!style) return "";
    return `${SECTION_CODES[section]}${level(style.titleSize)}${level(style.imageSize)}${level(style.spacing)}${level(style.textStyle)}`;
  }).join("");

  return encoded ? `${base}@${encoded}`.slice(0, 80) : base.slice(0, 80);
}

export function visualLevelScale(value, kind = "title") {
  const selected = level(value);
  const scales = {
    title: { 1: 0.84, 2: 1, 3: 1.18 },
    image: { 1: 0.78, 2: 1, 3: 1.22 },
    // Keep section density noticeable but bounded so compact/airy choices stay responsive-safe.
    spacing: { 1: 0.88, 2: 1, 3: 1.14 },
  };
  return scales[kind]?.[selected] || 1;
}

export function segmentVisualScale(themeOrTemplate, sectionId, kind = "title") {
  const visual = getSegmentVisualStyle(themeOrTemplate, sectionId);
  const key = kind === "title" ? "titleSize" : kind === "image" ? "imageSize" : "spacing";
  return visualLevelScale(visual[key], kind);
}

export function segmentCssVariables(theme, sectionId) {
  const visual = getSegmentVisualStyle(theme, sectionId);
  const titleScale = visualLevelScale(visual.titleSize, "title");
  const imageScale = visualLevelScale(visual.imageSize, "image");
  const spacingScale = visualLevelScale(visual.spacing, "spacing");
  const textStyles = {
    1: { weight: 760, spacing: "-.012em" },
    2: { weight: 900, spacing: "-.03em" },
    3: { weight: 950, spacing: "-.055em" },
  };
  const text = textStyles[level(visual.textStyle)] || textStyles[2];
  const vars = {
    "--wp-segment-image-scale": imageScale,
    "--wp-segment-title-weight": text.weight,
    "--wp-segment-letter-spacing": text.spacing,
    "--wp-hero-title-mobile": `${3.4 * titleScale}rem`,
    "--wp-hero-title-desktop": `${6 * titleScale}rem`,
    "--wp-navbar-title-mobile": `${1 * titleScale}rem`,
    "--wp-navbar-title-desktop": `${1.25 * titleScale}rem`,
  };

  [24, 30, 31, 32, 34, 40, 42, 44, 45, 46, 52].forEach((size) => {
    vars[`--wp-title-${size}`] = `${Math.round(size * titleScale * 100) / 100}px`;
  });
  [40, 48, 56, 64].forEach((size) => {
    vars[`--wp-space-${size}`] = `${Math.round(size * spacingScale * 100) / 100}px`;
  });
  [48, 62, 76, 122, 132, 150, 160, 165, 190, 205, 230, 300].forEach((size) => {
    vars[`--wp-image-${size}`] = `${Math.round(size * imageScale * 100) / 100}px`;
  });
  [610, 690, 720, 820].forEach((size) => {
    vars[`--wp-height-${size}`] = `${Math.round(size * spacingScale * 100) / 100}px`;
  });

  return vars;
}

export const SEGMENT_LABELS = {
  navbar: "Encabezado",
  hero: "Portada principal",
  spotlight: "Bloque destacado",
  featured: "Productos destacados",
  brands: "Marcas",
  categories: "Categorías",
  about: "Sobre el negocio",
  highlights: "Por qué elegirnos",
  contact: "Contacto",
  footer: "Pie de página",
  floatingWhatsapp: "WhatsApp flotante",
};
